import type { CronTimeFrame, RepeatingSchedule, ScheduleSummary } from '@/types/schedule';

import { type ScheduleTimeDisplayMode, formatAdvancedCronFrequency } from '../resolvers';
import { formatAdvancedCronFrequencyUtc } from '../resolvers/advancedCronResolver';
import { describeUtcRecurringSchedule } from './recurringCronSchedule';
import { convertUTCToLocalTime, formatTimezoneLabel } from './timezoneData';

export type { ScheduleTimeDisplayMode };

export {
  formatAdvancedCronFrequency,
  formatAdvancedCronFrequencyUtc,
  formatDailyScheduleFrequency,
  formatRepeatingScheduleFrequency,
  formatScheduleFrequency,
} from '../resolvers';

function formatLocalTime12h(hour: number, minute: number): string {
  const h12 = hour % 12 || 12;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const minStr = minute.toString().padStart(2, '0');
  return `${h12}:${minStr} ${ampm}`;
}

function formatUtcTimeLabel(timeUTC: string): string {
  const [hStr, mStr] = timeUTC.split(':');
  return formatLocalTime12h(parseInt(hStr, 10), parseInt(mStr, 10));
}

function formatStoredUtcInScheduleLocal(timeUTC: string, timeZone: string): string {
  const [hStr, mStr] = timeUTC.split(':');
  const local = convertUTCToLocalTime(parseInt(hStr, 10), parseInt(mStr, 10), timeZone);
  return formatLocalTime12h(local.hour, local.minute);
}

function formatRepeatingIntervalBase(
  freq: number,
  frequencyUnit: RepeatingSchedule['frequencyUnit']
): string {
  const unit = frequencyUnit === 'hour' ? 'hr' : 'min';
  const plural = freq !== 1 ? 's' : '';
  return freq === 0 ? `Every 0 ${unit}s` : `Every ${freq} ${unit}${plural}`;
}

/**
 * Format a cron expression as a human-readable frequency string.
 */
export function formatCronScheduleFrequency(
  cronExpression: string,
  cronExplain: string | undefined,
  cronTimeZone: string | undefined,
  mode: ScheduleTimeDisplayMode,
  viewerTimeZone?: string,
  cronTimeFrame?: CronTimeFrame | null
): string {
  return formatAdvancedCronFrequency(
    cronExpression,
    cronExplain,
    cronTimeZone,
    mode,
    viewerTimeZone,
    cronTimeFrame
  );
}

/**
 * Tooltip text: how the schedule was configured (wall clock + timezone at save time).
 */
export function formatConfiguredScheduleFrequencyTooltip(
  schedule: ScheduleSummary
): string | undefined {
  if (schedule.dailySchedule) {
    const { timeUTC, timeZone } = schedule.dailySchedule;
    const tz = timeZone?.trim();
    if (tz) {
      return `Configured: Daily at ${formatStoredUtcInScheduleLocal(timeUTC, tz)} ${formatTimezoneLabel(tz)}`;
    }
    return `Configured: Daily at ${formatUtcTimeLabel(timeUTC)} UTC`;
  }

  if (schedule.repeatingSchedule) {
    const { frequency, frequencyUnit, startingAt, timeZone } = schedule.repeatingSchedule;
    const base = formatRepeatingIntervalBase(frequency, frequencyUnit);

    if (!startingAt) {
      return `Configured: ${base}`;
    }

    const tz = timeZone?.trim();
    if (tz) {
      return `Configured: ${base}, starting at ${formatStoredUtcInScheduleLocal(startingAt, tz)} ${formatTimezoneLabel(tz)}`;
    }

    return `Configured: ${base}, starting at ${formatUtcTimeLabel(startingAt)} UTC (timezone not set)`;
  }

  if (schedule.cronExpression || schedule.cronExplain) {
    const tz = schedule.cronTimeZone?.trim();
    const utcRecurring = schedule.cronExpression
      ? describeUtcRecurringSchedule(schedule.cronExpression)
      : null;
    const utcDescription =
      utcRecurring ??
      formatAdvancedCronFrequencyUtc(schedule.cronExpression ?? '', schedule.cronExplain).replace(
        /\s*UTC\s*$/i,
        ''
      );
    if (!utcDescription) {
      return undefined;
    }
    if (tz) {
      const configured = formatAdvancedCronFrequency(
        schedule.cronExpression ?? '',
        schedule.cronExplain,
        tz,
        'local',
        tz,
        schedule.cronTimeFrame
      );
      return `Configured: ${configured} ${formatTimezoneLabel(tz)}`;
    }
    return `Configured: ${utcDescription} UTC (timezone not set)`;
  }

  if (schedule.frequency) {
    return `Configured: ${schedule.frequency}`;
  }

  return undefined;
}
